import React, {FC, useEffect, useState} from "react"
import {models} from "powerbi-client"
import {PowerBIEmbed} from "powerbi-client-react"

import FormattedMessage from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {PowerbiReportType, PowerbiReportDetails} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import adminTracking from "@irati-tracking/admin"
import {H6, Label} from "@irati/ui/src/Typography"
import {Tooltip, OverlayPlacement} from "@irati/ui/src/Tooltip"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import ErrorText from "@irati/ui/src/ErrorText"
import Spacing from "@irati/ui/src/Spacing"
import {usePowerbiReportDetails} from "@irati/powerbi/services/hooks"
import authentication from "@irati/auth"

import ShareLink from "../../ShareLink"
import {TooltipWrapper, PowerbiWrapper} from "./styled"

interface Props {
  onlineGP?: boolean
}

const BenefitsOverview: FC<Props> = ({onlineGP}) => {
  const [reportData, setReportData] = useState<PowerbiReportDetails | null>(null)
  const [error, setError] = useState("")
  const powerbiReport = usePowerbiReportDetails({
    businessId: authentication.business().uuid,
    type: onlineGP ? PowerbiReportType.OnlineGp : PowerbiReportType.Benefits
  })
  const theme = useTheme()

  useEffect(() => {
    if (powerbiReport?.data) {
      adminTracking.viewBenefitsReport()
      setReportData(powerbiReport?.data)
    }
  }, [powerbiReport?.data])

  useEffect(() => {
    if (powerbiReport?.error) {
      setError("errors.PBCPVUnknown")
    }
  }, [powerbiReport?.error])

  return (
    <Loading load={powerbiReport?.load}>
      {reportData && !error && (
        <>
          <PowerbiWrapper justifyContent="center" alignItems="center" width="100%" marginTop={3}>
            <PowerBIEmbed
              embedConfig={{
                type: "report",
                id: reportData.reportId || "",
                embedUrl: reportData.embedUrl || "",
                accessToken: reportData.embedToken || "",
                tokenType: models.TokenType.Embed,
                settings: {
                  filterPaneEnabled: false,
                  navContentPaneEnabled: false,
                  background: models.BackgroundType.Transparent
                }
              }}
              cssClassName="powerbiContainer"
              getEmbeddedComponent={(embeddedReport) => {
                embeddedReport.setComponentTitle(reportData.reportName)
              }}
            />
          </PowerbiWrapper>
          <Flex justifyContent="space-between" marginTop={2}>
            <Label>
              <FormattedMessage id="admin.reporting.popularPerks.dataInfo" />
            </Label>
            <Flex>
              <ShareLink />
              <TooltipWrapper marginLeft={2} justifyContent="center" alignItems="center" padding={[1, 2.5]}>
                <H6 color={theme.colors.primary2} marginRight>
                  <FormattedMessage id="admin.reporting.benefits.tooltip.btn" />
                </H6>
                <Tooltip
                  placement={OverlayPlacement.Left}
                  tooltipChildren={
                    <Flex direction="column" marginLeft={0.5}>
                      <H6 color={theme.colors.primary2} marginTop>
                        <FormattedMessage id="admin.reporting.benefits.tooltip.text1" />
                      </H6>
                      <Label marginLeft marginTop>
                        <FormattedMessage id="admin.reporting.benefits.tooltip.text2" />
                      </Label>
                      <Label marginLeft marginTop={0.5}>
                        <FormattedMessage id="admin.reporting.benefits.tooltip.text3" />
                      </Label>
                      <Label marginLeft marginTop={0.5}>
                        <FormattedMessage id="admin.reporting.benefits.tooltip.text4" />
                      </Label>
                    </Flex>
                  }>
                  <Icon marginLeft={0.5} icon={IconTypes.infoBlack} size={18} />
                </Tooltip>
              </TooltipWrapper>
            </Flex>
          </Flex>
        </>
      )}
      {error && (
        <Spacing marginTop={2}>
          <ErrorText>
            <FormattedMessage id={error} />
          </ErrorText>
        </Spacing>
      )}
    </Loading>
  )
}

export default BenefitsOverview
